import { useQuery } from '@tanstack/react-query'
import { billingApi } from '@/api/billing'
import { formatNIS } from '@/utils/currency'
import { formatDate } from '@/utils/date'
import { Loading } from '@/components/ui'

interface Props {
  orgId: string
}

export default function AdjustmentsBlock({ orgId }: Props) {
  const { data: rows, isLoading } = useQuery({
    queryKey: ['admin-billing-adjustments', orgId],
    queryFn: () => billingApi.listAdjustments(orgId),
    staleTime: 60_000,
  })

  return (
    <div className="card p-6 border border-navy-border bg-[#1a1d27]">
      <h3 className="text-sm font-semibold text-ink-primary mb-4">Adjustments</h3>
      {isLoading ? (
        <Loading text="Loading adjustments…" />
      ) : !rows?.length ? (
        <p className="text-sm text-ink-secondary">No adjustments.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((a) => (
            <li
              key={a.id}
              className="flex flex-wrap items-baseline justify-between gap-2 border-b border-navy-border/50 pb-3 last:border-0 last:pb-0"
            >
              <div>
                <p className="font-medium text-ink-primary">{a.label}</p>
                <p className="text-xs text-ink-muted mt-0.5">
                  {a.recurring ? 'Recurring' : 'One-off'}
                  {a.applies_from ? ` · from ${formatDate(a.applies_from)}` : ''}
                  {a.applies_until ? ` · until ${formatDate(a.applies_until)}` : ''}
                </p>
              </div>
              <p className={`text-sm font-semibold num ${a.amount_agorot < 0 ? 'text-teal-400' : 'text-ink-primary'}`}>
                {formatNIS(a.amount_agorot)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
